import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ArrowLeft, Receipt, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { creditDebitNoteApiService, CreditDebitNoteHeaderDto, CreditDebitNoteDetailDto, CreditDebitNoteItem, NoteType, CompleteCreditDebitNoteCreateRequest, CompleteCreditDebitNoteUpdateRequest } from '../services/creditDebitNoteApi';
import { invoiceApiService } from '../services/invoiceApi';
import { branchApiService, Branch } from '../services/branchApi';
import { customerApiService, Customer } from '../services/customerApi';
import { hsnApiService, HsnCode } from '../services/hsnApi';
import { taxRateHistoryApiService } from '../services/taxRateHistoryApi';
import { endgoodApiService } from '../services/endgoodApi';
import type { InvoiceHeader, InvoiceDetails, InvoiceHeaderListItem, OtherCharges } from '../services/invoiceApi';
import { useBranch } from '../contexts/BranchContext';
import { formatDate } from '../utils/dateUtils';
import {
  grandTotalTaxFromLines,
  isLegacyTaxHeaderMismatch,
  lineAmountFromQtyRate,
  roundLineTaxAmounts,
  roundMoney2,
  roundRate2,
} from '../utils/taxUtils';
import {
  canEditInvoiceDocument,
  isEInvoicedCancelledDocumentStatus,
} from '../utils/documentStatusUtils';

// Backend may store tax rate as percentage (18) or basis points (1800 = 18%). Normalize for calculation and display.
function toEffectiveRate(value: number): number {
  if (value == null || Number.isNaN(value)) return 0;
  return value > 100 ? value / 100 : value;
}
function toDisplayRate(value: number): number {
  if (value == null || Number.isNaN(value)) return 0;
  return value > 100 ? value / 100 : value;
}

function amountInWords(num: number): string {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convertHundreds = (n: number): string => {
    let result = '';
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + ' ';
      n %= 10;
    }
    if (n > 0) result += ones[n] + ' ';
    return result.trim();
  };

  if (num === 0) return 'Zero';
  const whole = Math.floor(num);
  const paise = Math.round((num % 1) * 100);
  const numStr = whole.toString();
  let result = '';

  if (numStr.length > 7) {
    const crores = parseInt(numStr.slice(0, numStr.length - 7), 10);
    if (crores > 0) result += convertHundreds(crores) + ' Crore ';
  }
  if (numStr.length > 5) {
    const lakhs = parseInt(numStr.slice(Math.max(0, numStr.length - 7), numStr.length - 5), 10);
    if (lakhs > 0) result += convertHundreds(lakhs) + ' Lakh ';
  }
  if (numStr.length > 3) {
    const thousands = parseInt(numStr.slice(Math.max(0, numStr.length - 5), numStr.length - 3), 10);
    if (thousands > 0) result += convertHundreds(thousands) + ' Thousand ';
  }
  const hundreds = parseInt(numStr.slice(Math.max(0, numStr.length - 3)), 10);
  if (hundreds > 0) result += convertHundreds(hundreds);

  result = result.trim() + ' Rupees';
  if (paise > 0) result += ' and ' + convertHundreds(paise) + ' Paise';
  return result.trim();
}

interface HsnWiseRow {
  hsn: string;
  taxableValue: number;
  cgstRate: number;
  cgstAmount: number;
  sgstRate: number;
  sgstAmount: number;
  igstRate: number;
  igstAmount: number;
  totalTaxAmount: number;
}

function normalizeNoteType(value: unknown): NoteType {
  const v = String(value ?? '').toUpperCase();
  if (v === 'DEBIT_NOTE' || v === 'DEBIT') return 'DEBIT_NOTE';
  return 'CREDIT_NOTE';
}

function getNoteNumber(header: CreditDebitNoteHeaderDto): string {
  return (
    header.noteNumber ??
    (header as { creditDebitNoteNo?: string }).creditDebitNoteNo ??
    (header.id != null ? `#${header.id}` : '')
  );
}

function hasEndgoodId(endgoodId: unknown): boolean {
  return endgoodId != null && Number(endgoodId) > 0;
}

function isTruthyManualFlag(value: unknown): boolean | undefined {
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (v === 'true' || v === '1') return true;
    if (v === 'false' || v === '0') return false;
  }
  return undefined;
}

/** Manual line: explicit isManual from API, or no endgood with a free-text description. */
function creditDebitNoteDetailIsManual(
  detail: Pick<
    CreditDebitNoteDetailDto,
    'isManual' | 'endgoodId' | 'itemDescription' | 'item_description' | 'goodName' | 'endgoodName'
  >
): boolean {
  const manualFlag = isTruthyManualFlag(detail.isManual);
  if (manualFlag === true) return true;
  if (manualFlag === false) return false;
  if (hasEndgoodId(detail.endgoodId)) return false;
  const desc = String(
    detail.itemDescription ?? detail.item_description ?? detail.goodName ?? detail.endgoodName ?? ''
  ).trim();
  return desc.length > 0;
}

function lineRowAllowsHsnEdit(row: LineRow): boolean {
  return creditDebitNoteDetailIsManual({
    isManual: row.isManual,
    endgoodId: row.endgoodId,
    itemDescription: row.goodName,
    goodName: row.goodName,
  });
}

/** True when line cgst/sgst/igst are rupee amounts (saved payload), not percentage rates. */
function lineTaxFieldsAreAmounts(
  details: Array<{ cgst?: number; sgst?: number; igst?: number }>,
  headerTax: number
): boolean {
  const sumLineTaxFields = details.reduce(
    (s, i) => s + (i.cgst ?? 0) + (i.sgst ?? 0) + (i.igst ?? 0),
    0
  );
  if (headerTax > 0) {
    const diff = Math.abs(sumLineTaxFields - headerTax);
    return diff <= 0.01 * Math.max(headerTax, 1);
  }
  return details.some(
    (i) => (i.cgst ?? 0) > 100 || (i.sgst ?? 0) > 100 || (i.igst ?? 0) > 100
  );
}

async function resolveHsnTaxPercentages(
  hsnCode: string,
  dateForApi: string,
  sameState: boolean,
  hsnCodes: HsnCode[]
): Promise<{ cgst: number; sgst: number; igst: number }> {
  const trimmed = hsnCode.trim();
  const hsnRecord = trimmed ? hsnCodes.find((h) => (h.hsnCode ?? '') === trimmed) : null;
  if (!hsnRecord) return { cgst: 0, sgst: 0, igst: 0 };
  try {
    const applicable = await taxRateHistoryApiService.getApplicableTaxRate(hsnRecord.id, dateForApi);
    if (sameState) {
      return {
        cgst: applicable.cgstRate ?? 0,
        sgst: applicable.sgstRate ?? 0,
        igst: 0,
      };
    }
    return {
      cgst: 0,
      sgst: 0,
      igst: applicable.igstRate ?? 0,
    };
  } catch {
    if (sameState) {
      return {
        cgst: hsnRecord.currentCgstRate ?? 0,
        sgst: hsnRecord.currentSgstRate ?? 0,
        igst: 0,
      };
    }
    return {
      cgst: 0,
      sgst: 0,
      igst: hsnRecord.currentIgstRate ?? 0,
    };
  }
}

function taxRatesFromStoredLine(
  detail: { qty: number; rate: number; cgst?: number; sgst?: number; igst?: number },
  taxFieldsAreAmounts: boolean,
  sameState: boolean
): { cgst: number; sgst: number; igst: number } {
  const taxable = (detail.qty ?? 0) * (detail.rate ?? 0);
  if (taxFieldsAreAmounts && taxable > 0) {
    if (sameState) {
      return {
        cgst: ((detail.cgst ?? 0) / taxable) * 100,
        sgst: ((detail.sgst ?? 0) / taxable) * 100,
        igst: 0,
      };
    }
    return {
      cgst: 0,
      sgst: 0,
      igst: ((detail.igst ?? 0) / taxable) * 100,
    };
  }
  return {
    cgst: toDisplayRate(detail.cgst ?? 0),
    sgst: toDisplayRate(detail.sgst ?? 0),
    igst: toDisplayRate(detail.igst ?? 0),
  };
}

type LineRow = {
  selected: boolean;
  endgoodId: number;
  goodName: string;
  hsn: string;
  invoicedQty: number;
  invoicedRate: number;
  qty: number;
  rate: number;
  uom: string;
  /** HSN tax % (or basis points); used for UI — same semantics as invoice line before save */
  cgst?: number;
  sgst?: number;
  igst?: number;
  typeOfGood?: string;
  /** When true, HSN can be changed via dropdown on edit */
  isManual?: boolean;
  /** Loaded from invoice other charges (not a goods line) */
  isOtherCharge?: boolean;
};

function getInvoiceOtherChargeLabel(charge: OtherCharges): string {
  const desc =
    String(charge.chargeDescription ?? '').trim() ||
    String(charge.chargeMaster?.description ?? '').trim() ||
    String(charge.chargeName ?? charge.chargeMaster?.chargeName ?? '').trim() ||
    String(charge.chargeCode ?? charge.chargeMaster?.chargeCode ?? '').trim();
  return desc || (charge.id != null ? `Other charge #${charge.id}` : 'Other charge');
}

/** Match invoice detail payload: cgst/sgst/igst are rupee amounts, not rates. */
function lineGstAmountsForApi(l: LineRow): { cgst: number; sgst: number; igst: number } {
  const base = lineAmountFromQtyRate(l.qty, l.rate);
  return roundLineTaxAmounts({
    cgst: (base * toEffectiveRate(l.cgst ?? 0)) / 100,
    sgst: (base * toEffectiveRate(l.sgst ?? 0)) / 100,
    igst: (base * toEffectiveRate(l.igst ?? 0)) / 100,
  });
}

const REASON_OPTIONS = [
  { value: 'NOT_APPLICABLE', label: 'Not Applicable', payload: 'Not Applicable' },
  { value: '01_SALES_RETURN', label: '01-Sales Return', payload: '01-Sales Return' },
  { value: '02_POST_SALE_DISCOUNT', label: '02-Post Sale Discount', payload: '02-Post Sale Discount' },
  { value: '03_DEFICIENCY_IN_SERVICES', label: '03-Deficiency in services', payload: '03-Deficiency in services' },
  { value: '04_CORRECTION_IN_INVOICE', label: '04-Correction in Invoice', payload: '04-Correction in Invoice' },
  { value: '05_CHANGE_IN_POS', label: '05-Change in POS', payload: '05-Change in POS' },
  { value: '06_FINALIZATION_OF_PROVISIONAL_ASSESSMENT', label: '06-Finalization of Provisional assessment', payload: '06-Finalization of Provisional assessment' },
  { value: 'OTHERS', label: '07-Others', payload: 'OTHERS' },
] as const;

type ReasonOptionValue = (typeof REASON_OPTIONS)[number]['value'];

function getReferenceInvoiceId(invoice: InvoiceHeaderListItem): number | undefined {
  return invoice.id ?? invoice.invoiceId ?? invoice.invoiceHeaderId;
}

export function CreditDebitNoteFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  const isEdit = Boolean(id);

  const [noteType, setNoteType] = useState<NoteType>('CREDIT_NOTE');
  const [creditDate, setCreditDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [hsnTaxRateDate, setHsnTaxRateDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [invoiceId, setInvoiceId] = useState<string>('');
  const [invoices, setInvoices] = useState<InvoiceHeaderListItem[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceHeader | null>(null);
  const [lines, setLines] = useState<LineRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingHeader, setExistingHeader] = useState<CreditDebitNoteHeaderDto | null>(null);
  const [branchDetails, setBranchDetails] = useState<Branch | null>(null);
  const [qtyDrafts, setQtyDrafts] = useState<Record<number, string>>({});
  const [rateDrafts, setRateDrafts] = useState<Record<number, string>>({});
  const [reason, setReason] = useState<ReasonOptionValue>('NOT_APPLICABLE');
  const [otherReason, setOtherReason] = useState('');
  const [otherReference, setOtherReference] = useState('');
  const [hsnCodes, setHsnCodes] = useState<HsnCode[]>([]);
  const [billToCustomer, setBillToCustomer] = useState<Customer | null>(null);
  const invoiceOptions = useMemo(
    () =>
      invoices
        .map((invoice) => {
          const resolvedId = getReferenceInvoiceId(invoice);
          if (resolvedId == null) return null;
          return {
            ...invoice,
            resolvedId,
          };
        })
        .filter((invoice): invoice is InvoiceHeaderListItem & { resolvedId: number } => invoice != null),
    [invoices]
  );

  useEffect(() => {
    if (selectedBranch == null) return;
    setLoadingInvoices(true);
    invoiceApiService
      .getReferenceInvoices(selectedBranch)
      .then((res) => {
        if (res?.success && res?.data) setInvoices(Array.isArray(res.data) ? res.data : []);
        else setInvoices([]);
      })
      .catch(() => setInvoices([]))
      .finally(() => setLoadingInvoices(false));
  }, [selectedBranch]);

  useEffect(() => {
    if (selectedBranch == null) return;
    branchApiService.getBranchById(String(selectedBranch)).then((b) => setBranchDetails(b ?? null)).catch(() => setBranchDetails(null));
  }, [selectedBranch]);

  useEffect(() => {
    hsnApiService.getHsnCodes().then((list) => {
      setHsnCodes(Array.isArray(list) ? list : []);
    }).catch(() => setHsnCodes([]));
  }, []);

  const billToId = isEdit && existingHeader
    ? existingHeader.billTo
    : selectedInvoice?.billTo;

  useEffect(() => {
    if (billToId == null) {
      setBillToCustomer(null);
      return;
    }
    customerApiService.getCustomerById(String(billToId))
      .then((res) => setBillToCustomer(res?.success && res?.data ? res.data : null))
      .catch(() => setBillToCustomer(null));
  }, [billToId]);

  const sameState = useMemo(() => {
    if (!branchDetails || !billToCustomer) return true;
    return (branchDetails.stateCode ?? '') === (billToCustomer.stateCode ?? '');
  }, [branchDetails, billToCustomer]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    creditDebitNoteApiService
      .getById(id)
      .then((h) => {
        if (h && !canEditInvoiceDocument(h.status)) {
          toast.error(
            isEInvoicedCancelledDocumentStatus(h.status)
              ? 'This note’s e-invoice was cancelled and cannot be edited.'
              : 'This note cannot be edited in its current status.'
          );
          navigate(`/invoices/credit-debit-notes/${id}/view`, { replace: true });
          return;
        }
        setExistingHeader(h ?? null);
        if (h) {
          setNoteType(normalizeNoteType(h.noteType));
          const noteDateStr = h.noteDate
            ? String(h.noteDate).slice(0, 10)
            : (h as { creditDate?: string }).creditDate
              ? String((h as { creditDate: string }).creditDate).slice(0, 10)
              : '';
          setCreditDate(noteDateStr);
          const taxDateStr = h.taxDate
            ? String(h.taxDate).slice(0, 10)
            : noteDateStr;
          setHsnTaxRateDate(taxDateStr || new Date().toISOString().slice(0, 10));
          setInvoiceId((h.referenceInvoiceId ?? (h as { invoiceHeaderId?: number }).invoiceHeaderId)?.toString() ?? '');
          const reasonValue = String(h.reason ?? '').trim();
          const matchedReason = REASON_OPTIONS.find((option) => option.payload === reasonValue);
          if (matchedReason) {
            setReason(matchedReason.value);
            setOtherReason('');
          } else if (reasonValue.toUpperCase().startsWith('OTHERS:')) {
            setReason('OTHERS');
            setOtherReason(reasonValue.slice('OTHERS:'.length).trim());
          } else {
            setReason('NOT_APPLICABLE');
            setOtherReason('');
          }
          setOtherReference(String(h.otherReference ?? '').trim());
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!existingHeader?.details?.length) return;
    let cancelled = false;

    const loadEditLines = async () => {
      const details = existingHeader.details!;
      const noteDate =
        existingHeader.noteDate
          ? String(existingHeader.noteDate).slice(0, 10)
          : creditDate.split('T')[0] || creditDate;
      const taxDateForApi = existingHeader.taxDate
        ? String(existingHeader.taxDate).slice(0, 10)
        : noteDate;
      const dateForApi = taxDateForApi.split('T')[0] || taxDateForApi;
      const headerTax =
        existingHeader.grandTotalTax != null && !Number.isNaN(Number(existingHeader.grandTotalTax))
          ? Number(existingHeader.grandTotalTax)
          : 0;
      const taxFieldsAreAmounts = lineTaxFieldsAreAmounts(details, headerTax);

      const branch =
        branchDetails ??
        (selectedBranch != null ? await branchApiService.getBranchById(String(selectedBranch)) : null);
      const billToId = existingHeader.billTo;
      const billToRes =
        billToId != null
          ? await customerApiService.getCustomerById(String(billToId))
          : { success: false, data: null };
      const billToCustomer: Customer | null =
        billToRes?.success && billToRes?.data ? billToRes.data : null;
      const sameState =
        branch && billToCustomer
          ? (branch.stateCode ?? '') === (billToCustomer.stateCode ?? '')
          : true;

      const hsnCodesList = await hsnApiService.getHsnCodes();
      const hsnCodes: HsnCode[] = Array.isArray(hsnCodesList) ? hsnCodesList : [];

      const mappedLines = await Promise.all(
        details.map(async (i) => {
          const hsnCode = (i.hsn ?? '').trim();
          const hsnRecord = hsnCode ? hsnCodes.find((h) => (h.hsnCode ?? '') === hsnCode) : null;
          const rates = taxFieldsAreAmounts
            ? taxRatesFromStoredLine(i, true, sameState)
            : hsnRecord
              ? await resolveHsnTaxPercentages(hsnCode, dateForApi, sameState, hsnCodes)
              : taxRatesFromStoredLine(i, false, sameState);
          return {
            selected: true,
            endgoodId: i.endgoodId ?? 0,
            goodName: String(
              i.itemDescription ?? i.item_description ?? i.endgoodName ?? i.goodName ?? ''
            ).trim(),
            hsn: i.hsn ?? '',
            invoicedQty: i.qty,
            invoicedRate: roundRate2(i.rate ?? 0),
            qty: i.qty,
            rate: roundRate2(i.rate ?? 0),
            uom: i.uom ?? 'Nos',
            cgst: rates.cgst,
            sgst: rates.sgst,
            igst: rates.igst,
            typeOfGood: i.typeOfGood,
            isManual: creditDebitNoteDetailIsManual(i),
          };
        })
      );

      if (cancelled) return;
      setLines(mappedLines);
      setQtyDrafts(
        Object.fromEntries(
          mappedLines.map((line, index) => [index, line.qty === 0 ? '' : String(line.qty)])
        )
      );
      setRateDrafts(
        Object.fromEntries(
          mappedLines.map((line, index) => [index, line.rate === 0 ? '' : String(line.rate)])
        )
      );
    };

    void loadEditLines();
    return () => {
      cancelled = true;
    };
  }, [existingHeader, branchDetails, selectedBranch, creditDate]);

  const loadInvoiceLines = async () => {
    if (!invoiceId) {
      toast.error('Select an invoice first');
      return;
    }
    setLoading(true);
    try {
      const [headerRes, detailsRes, chargesRes, branchData, hsnCodesList] = await Promise.all([
        invoiceApiService.getInvoiceHeaderById(invoiceId),
        invoiceApiService.getInvoiceDetailsByHeader(invoiceId),
        invoiceApiService.getOtherChargesByInvoice(invoiceId).catch(() => ({
          success: false,
          data: [] as OtherCharges[],
        })),
        branchDetails
          ? Promise.resolve(branchDetails)
          : selectedBranch != null
            ? branchApiService.getBranchById(String(selectedBranch))
            : Promise.resolve(null),
        hsnApiService.getHsnCodes(),
      ]);
      const invoiceHeader = headerRes?.success && headerRes?.data ? headerRes.data : null;
      if (!invoiceHeader) {
        toast.error('Invoice not found');
        setLines([]);
        return;
      }
      const details =
        detailsRes?.success && Array.isArray(detailsRes.data)
          ? (detailsRes.data as InvoiceDetails[])
          : [];
      const chargesFromApi =
        chargesRes?.success && Array.isArray(chargesRes.data) ? chargesRes.data : [];
      const chargesFromHeader = Array.isArray(invoiceHeader.otherCharges)
        ? invoiceHeader.otherCharges
        : [];
      const otherCharges = chargesFromApi.length > 0 ? chargesFromApi : chargesFromHeader;

      if (details.length === 0 && otherCharges.length === 0) {
        setLines([]);
        toast.error('No line items or other charges found on this invoice');
        return;
      }

      const billToCustomerRes =
        invoiceHeader.billTo != null
          ? await customerApiService.getCustomerById(String(invoiceHeader.billTo))
          : { success: false, data: null };
      const branch =
        branchData ??
        (selectedBranch != null
          ? await branchApiService.getBranchById(String(selectedBranch))
          : null);
      const billToCustomer: Customer | null =
        billToCustomerRes?.success && billToCustomerRes?.data ? billToCustomerRes.data : null;
      const hsnCodes: HsnCode[] = Array.isArray(hsnCodesList) ? hsnCodesList : [];
      const sameState = branch && billToCustomer
        ? (branch.stateCode ?? '') === (billToCustomer.stateCode ?? '')
        : true;
      const dateForApi = creditDate.split('T')[0] || creditDate;

      const goodsLines = await Promise.all(
        details.map(async (d) => {
          const q = d.qty ?? 0;
          const r = d.rate ?? 0;
          const hsnCode = (d.hsn ?? '').trim();
          const { cgst: cgstPct, sgst: sgstPct, igst: igstPct } = await resolveHsnTaxPercentages(
            hsnCode,
            dateForApi,
            sameState,
            hsnCodes
          );
          const endgoodId = d.endgood?.id ?? d.endgoodId ?? 0;
          const goodName =
            (d.endgoodName ?? d.endgood?.endgoodName ?? '').toString().trim() ||
            (endgoodId ? `Item #${endgoodId}` : '');
          return {
            selected: true,
            endgoodId,
            goodName,
            hsn: d.hsn ?? '',
            invoicedQty: q,
            invoicedRate: r,
            qty: 0,
            rate: 0,
            uom: d.uom ?? 'Nos',
            cgst: cgstPct,
            sgst: sgstPct,
            igst: igstPct,
            typeOfGood: d.typeOfGood,
            isManual: Boolean(d.isManual) || (!hasEndgoodId(d.endgood?.id ?? d.endgoodId) && Boolean(goodName)),
            isOtherCharge: false,
          } as LineRow;
        })
      );

      const chargeLines: LineRow[] = otherCharges.map((charge) => {
        const amount = Number(charge.amount) || 0;
        const label = getInvoiceOtherChargeLabel(charge);
        return {
          selected: true,
          endgoodId: 0,
          goodName: label,
          hsn: '',
          invoicedQty: 1,
          invoicedRate: amount,
          qty: 0,
          rate: 0,
          uom: 'Nos',
          cgst: 0,
          sgst: 0,
          igst: 0,
          typeOfGood: 'OTHER_CHARGE',
          isManual: true,
          isOtherCharge: true,
        };
      });

      const linesWithRates = [...goodsLines, ...chargeLines];
      setLines(linesWithRates);
      setQtyDrafts(
        Object.fromEntries(linesWithRates.map((line, index) => [index, line.qty === 0 ? '' : String(line.qty)]))
      );
      setRateDrafts(
        Object.fromEntries(linesWithRates.map((line, index) => [index, line.rate === 0 ? '' : String(line.rate)]))
      );
      setSelectedInvoice(invoiceHeader);

      if (chargeLines.length > 0) {
        toast.success(
          `Loaded ${goodsLines.length} line item(s) and ${chargeLines.length} other charge(s)`
        );
      }
    } catch {
      setLines([]);
      toast.error('Failed to load invoice lines / other charges');
    } finally {
      setLoading(false);
    }
  };

  const toggleLine = (index: number) => {
    setLines((prev) => prev.map((r, i) => (i === index ? { ...r, selected: !r.selected } : r)));
  };
  const toggleAll = (checked: boolean) => {
    setLines((prev) => prev.map((r) => ({ ...r, selected: checked })));
  };

  const setLineQty = (index: number, value: number) => {
    setLines((prev) => prev.map((r, i) => (i === index ? { ...r, qty: value } : r)));
  };
  const setLineRate = (index: number, value: number) => {
    setLines((prev) => prev.map((r, i) => (i === index ? { ...r, rate: roundRate2(value) } : r)));
  };
  const isValidDecimalInput = (value: string) => /^\d*\.?\d*$/.test(value);
  const handleQtyDraftChange = (index: number, value: string) => {
    if (!isValidDecimalInput(value)) return;
    setQtyDrafts((prev) => ({ ...prev, [index]: value }));
    setLineQty(index, value === '' || value === '.' ? 0 : parseFloat(value));
  };
  const handleRateDraftChange = (index: number, value: string) => {
    if (!isValidDecimalInput(value)) return;
    setRateDrafts((prev) => ({ ...prev, [index]: value }));
    setLineRate(index, value === '' || value === '.' ? 0 : parseFloat(value));
  };

  const applyLineTaxRates = useCallback(
    async (index: number, hsnCode: string, dateForApi: string) => {
      const rates = await resolveHsnTaxPercentages(hsnCode, dateForApi, sameState, hsnCodes);
      setLines((prev) =>
        prev.map((row, i) =>
          i === index
            ? { ...row, hsn: hsnCode, cgst: rates.cgst, sgst: rates.sgst, igst: rates.igst }
            : row
        )
      );
    },
    [sameState, hsnCodes]
  );

  const handleLineHsnChange = useCallback(
    (index: number, hsnCode: string) => {
      const dateForApi = hsnTaxRateDate.split('T')[0] || hsnTaxRateDate;
      void applyLineTaxRates(index, hsnCode, dateForApi);
      setLines((prev) => prev.map((row, i) => (i === index ? { ...row, hsn: hsnCode } : row)));
    },
    [hsnTaxRateDate, applyLineTaxRates]
  );

  const handleHsnTaxRateDateChange = useCallback(
    (value: string) => {
      setHsnTaxRateDate(value);
      const dateForApi = value.split('T')[0] || value;
      setLines((prev) => {
        void (async () => {
          const next = await Promise.all(
            prev.map(async (row) => {
              if (!row.hsn?.trim()) return row;
              const rates = await resolveHsnTaxPercentages(row.hsn, dateForApi, sameState, hsnCodes);
              return { ...row, cgst: rates.cgst, sgst: rates.sgst, igst: rates.igst };
            })
          );
          setLines(next);
        })();
        return prev;
      });
    },
    [sameState, hsnCodes]
  );

  const selectedLines = lines.filter((l) => l.selected);
  const lineAmount = (l: LineRow) => lineAmountFromQtyRate(l.qty, l.rate);
  const selectedLineTaxes = selectedLines.map(lineGstAmountsForApi);
  const taxableAmount = selectedLines.reduce((s, l) => s + lineAmount(l), 0);
  const totalCgst = selectedLineTaxes.reduce((s, t) => s + t.cgst, 0);
  const totalSgst = selectedLineTaxes.reduce((s, t) => s + t.sgst, 0);
  const totalIgst = selectedLineTaxes.reduce((s, t) => s + t.igst, 0);
  const totalTaxAmount = grandTotalTaxFromLines(selectedLineTaxes);
  const grandTotal = roundMoney2(taxableAmount + totalTaxAmount);
  const isIgst = totalIgst > 0;

  const hsnWiseTaxDetails = useMemo((): HsnWiseRow[] => {
    const byHsn: Record<string, { taxableValue: number; cgstRate: number; cgstAmount: number; sgstRate: number; sgstAmount: number; igstRate: number; igstAmount: number }> = {};
    selectedLines.forEach((l) => {
      const amt = lineAmount(l);
      const tax = lineGstAmountsForApi(l);
      const hsn = l.hsn || 'N/A';
      if (!byHsn[hsn]) {
        byHsn[hsn] = {
          taxableValue: 0,
          cgstRate: toDisplayRate(l.cgst ?? 0),
          cgstAmount: 0,
          sgstRate: toDisplayRate(l.sgst ?? 0),
          sgstAmount: 0,
          igstRate: toDisplayRate(l.igst ?? 0),
          igstAmount: 0,
        };
      }
      byHsn[hsn].taxableValue += amt;
      byHsn[hsn].cgstAmount += tax.cgst;
      byHsn[hsn].sgstAmount += tax.sgst;
      byHsn[hsn].igstAmount += tax.igst;
    });
    return Object.entries(byHsn).map(([hsn, v]) => {
      const taxableValue = roundMoney2(v.taxableValue);
      const cgstAmount = roundMoney2(v.cgstAmount);
      const sgstAmount = roundMoney2(v.sgstAmount);
      const igstAmount = roundMoney2(v.igstAmount);
      return {
        hsn,
        taxableValue,
        cgstRate: taxableValue > 0 ? (cgstAmount / taxableValue) * 100 : v.cgstRate,
        cgstAmount,
        sgstRate: taxableValue > 0 ? (sgstAmount / taxableValue) * 100 : v.sgstRate,
        sgstAmount,
        igstRate: taxableValue > 0 ? (igstAmount / taxableValue) * 100 : v.igstRate,
        igstAmount,
        totalTaxAmount: roundMoney2(cgstAmount + sgstAmount + igstAmount),
      };
    });
  }, [selectedLines]);

  const handleSave = async () => {
    if (selectedBranch == null) {
      toast.error('Select a branch');
      return;
    }
    if (selectedLines.length === 0) {
      toast.error('Select at least one line item');
      return;
    }
    if (reason === 'OTHERS' && !otherReason.trim()) {
      toast.error('Enter a reason for Others');
      return;
    }
    const resolvedReason =
      reason === 'OTHERS'
        ? `OTHERS: ${otherReason.trim()}`
        : (REASON_OPTIONS.find((option) => option.value === reason)?.payload ?? 'Not Applicable');
    const detailsPayload = selectedLines.map((l) => {
      const tax = lineGstAmountsForApi(l);
      const rate = roundRate2(l.rate);
      const amount = lineAmountFromQtyRate(l.qty, rate);
      return {
        endgoodId: l.isManual ? null : (l.endgoodId || null),
        goodName: l.goodName,
        itemDescription: lineRowAllowsHsnEdit(l) ? l.goodName : undefined,
        uom: l.uom,
        qty: l.qty,
        rate,
        hsn: l.hsn,
        cgst: tax.cgst,
        sgst: tax.sgst,
        igst: tax.igst,
        amount,
        typeOfGood: l.typeOfGood,
        isManual: lineRowAllowsHsnEdit(l),
      };
    });
    const taxableAmountResolved = detailsPayload.reduce((s, d) => s + (d.amount ?? 0), 0);

    const lineTaxSum = grandTotalTaxFromLines(detailsPayload);
    const loadedTaxDate =
      isEdit && existingHeader
        ? existingHeader.taxDate != null
          ? String(existingHeader.taxDate).slice(0, 10)
          : existingHeader.noteDate != null
            ? String(existingHeader.noteDate).slice(0, 10)
            : ''
        : '';
    const currentTaxDate = hsnTaxRateDate.split('T')[0] || hsnTaxRateDate;
    const taxDateChanged = isEdit && loadedTaxDate !== currentTaxDate;
    const headerTax =
      isEdit && existingHeader?.grandTotalTax != null && !Number.isNaN(Number(existingHeader.grandTotalTax))
        ? Number(existingHeader.grandTotalTax)
        : 0;
    const grandTotalTaxResolved = taxDateChanged
      ? lineTaxSum
      : isEdit && headerTax > 0 && isLegacyTaxHeaderMismatch(lineTaxSum, headerTax)
        ? headerTax
        : lineTaxSum;
    const grandTotalResolved = roundMoney2(taxableAmountResolved + grandTotalTaxResolved);

    const billTo = (isEdit && existingHeader ? existingHeader.billTo : selectedInvoice?.billTo) ?? 0;
    const shipTo = (isEdit && existingHeader ? existingHeader.shipTo : selectedInvoice?.shipTo ?? selectedInvoice?.billTo) ?? 0;

    if (isEdit && existingHeader) {
      setSaving(true);
      try {
        const updateData: CompleteCreditDebitNoteUpdateRequest = {
          id: existingHeader.id ?? parseInt(id!, 10),
          noteType,
          noteDate: creditDate || (existingHeader.noteDate ? String(existingHeader.noteDate).slice(0, 10) : (existingHeader as { creditDate?: string }).creditDate ? String((existingHeader as { creditDate: string }).creditDate).slice(0, 10) : ''),
          taxDate: hsnTaxRateDate.split('T')[0] || hsnTaxRateDate,
          referenceInvoiceId: existingHeader.referenceInvoiceId ?? (existingHeader as { invoiceHeaderId?: number }).invoiceHeaderId ?? parseInt(invoiceId, 10),
          branchId: selectedBranch,
          shipTo,
          billTo,
          reason: resolvedReason,
          otherReference: otherReference.trim() || undefined,
          totalAmount: taxableAmountResolved,
          grandTotalTax: grandTotalTaxResolved,
          grandTotal: grandTotalResolved,
          details: detailsPayload,
        };
        const response = await creditDebitNoteApiService.updateCompleteCreditDebitNote(id!, updateData);
        if (response.success && response.data?.id) {
          navigate(`/invoices/credit-debit-notes/${response.data.id}/view`);
        }
      } catch {
        // toasts in service
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!selectedInvoice || !invoiceId) {
      toast.error('Select an invoice and load lines');
      return;
    }
    setSaving(true);
    try {
      const requestData: CompleteCreditDebitNoteCreateRequest = {
        noteType,
        noteDate: creditDate,
        taxDate: creditDate.split('T')[0] || creditDate,
        referenceInvoiceId: parseInt(invoiceId, 10),
        branchId: selectedBranch,
        shipTo,
        billTo,
        reason: resolvedReason,
        otherReference: otherReference.trim() || undefined,
        totalAmount: taxableAmountResolved,
        grandTotalTax: grandTotalTaxResolved,
        grandTotal: grandTotalResolved,
        details: detailsPayload,
      };
      const response = await creditDebitNoteApiService.createCompleteCreditDebitNote(requestData);
      if (response.success && response.data?.id) {
        navigate(`/invoices/credit-debit-notes/${response.data.id}/view`);
      }
    } catch {
      // toasts in service
    } finally {
      setSaving(false);
    }
  };

  if (loading && isEdit) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={() => navigate('/invoices/credit-debit-notes')} className="border-primary-200">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      <Card>
        <CardHeader className="bg-primary-50/50 border-b border-primary-200">
          <CardTitle className="flex items-center gap-2 text-primary-800">
            <Receipt className="h-5 w-5" />
            {isEdit ? 'Edit' : 'New'} Credit / Debit Note
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {isEdit && existingHeader && (
              <div className="space-y-2 md:col-span-2">
                <Label>Note No</Label>
                <Input
                  value={getNoteNumber(existingHeader)}
                  readOnly
                  disabled
                  className="bg-gray-50 font-medium text-primary-800"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Note type</Label>
              <Select value={noteType} onValueChange={(v) => setNoteType(v as NoteType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CREDIT_NOTE">Credit Note</SelectItem>
                  <SelectItem value="DEBIT_NOTE">Debit Note</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Credit date</Label>
                  <Input type="date" value={creditDate} onChange={(e) => setCreditDate(e.target.value)} />
                </div>
                {isEdit && (
                  <div className="space-y-2">
                    <Label>Tax rate date</Label>
                    <Input
                      type="date"
                      value={hsnTaxRateDate}
                      onChange={(e) => handleHsnTaxRateDateChange(e.target.value)}
                      className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Invoice</Label>
              <Select
                value={invoiceId}
                onValueChange={setInvoiceId}
                disabled={isEdit}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingInvoices ? 'Loading...' : 'Select invoice'} />
                </SelectTrigger>
                <SelectContentWithSearch searchPlaceholder="Search invoice no / customer…" debounceMs={300}>
                  {invoiceOptions.map((inv) => (
                    <SelectItem key={inv.resolvedId} value={String(inv.resolvedId)}>
                      {inv.invoiceNo ?? inv.invoiceNumber ?? `#${inv.resolvedId}`} - {inv.invoiceDate ? formatDate(inv.invoiceDate) : ''}{inv.billToName ? ` - ${inv.billToName}` : ''}
                    </SelectItem>
                  ))}
                </SelectContentWithSearch>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Select value={reason} onValueChange={(value) => setReason(value as ReasonOptionValue)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {REASON_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {reason === 'OTHERS' && (
              <div className="space-y-2 md:col-span-2">
                <Label>Other reason</Label>
                <Input
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  placeholder="Enter reason"
                />
              </div>
            )}
            <div className="space-y-2 md:col-span-2">
              <Label>Other References</Label>
              <Input
                value={otherReference}
                onChange={(e) => setOtherReference(e.target.value)}
                placeholder="Enter other references"
              />
            </div>
            {!isEdit && (
              <div>
                <Button onClick={loadInvoiceLines} disabled={loading || !invoiceId}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Load invoice lines & charges
                </Button>
              </div>
            )}
          </div>

          {lines.length > 0 && (
            <>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Line items & other charges</h3>
                <Button variant="outline" size="sm" onClick={() => toggleAll(true)}>
                  Select all
                </Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-primary-50">
                    <TableHead className="w-12">Include</TableHead>
                    <TableHead className="font-semibold text-primary-800">Description</TableHead>
                    <TableHead className="font-semibold text-primary-800">HSN/SAC</TableHead>
                    <TableHead className="font-semibold text-primary-800 w-24">Invoiced Qty</TableHead>
                    <TableHead className="font-semibold text-primary-800 w-24">Invoiced Rate</TableHead>
                    <TableHead className="font-semibold text-primary-800 w-28">New Qty</TableHead>
                    <TableHead className="font-semibold text-primary-800 w-28">New Rate</TableHead>
                    <TableHead className="font-semibold text-primary-800 w-28">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={row.selected}
                          onChange={() => toggleLine(idx)}
                          className="rounded border-primary-300"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          <span>{row.goodName}</span>
                          {row.isOtherCharge ? (
                            <span className="text-xs text-muted-foreground">Other charge</span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        {isEdit && lineRowAllowsHsnEdit(row) ? (
                          <Select
                            value={row.hsn || undefined}
                            onValueChange={(value) => void handleLineHsnChange(idx, value)}
                          >
                            <SelectTrigger className="h-8 min-w-[140px] border-primary-200">
                              <SelectValue placeholder="Select HSN" />
                            </SelectTrigger>
                            <SelectContentWithSearch searchPlaceholder="Search HSN..." debounceMs={300}>
                              {row.hsn &&
                                !hsnCodes.some((h) => h.hsnCode === row.hsn) && (
                                  <SelectItem value={row.hsn}>{row.hsn}</SelectItem>
                                )}
                              {hsnCodes.map((h) => (
                                <SelectItem key={h.id} value={h.hsnCode}>
                                  {h.hsnCode}
                                  {h.description ? ` - ${h.description}` : ''}
                                </SelectItem>
                              ))}
                            </SelectContentWithSearch>
                          </Select>
                        ) : (
                          row.hsn
                        )}
                      </TableCell>
                      <TableCell className="text-right">{row.invoicedQty}</TableCell>
                      <TableCell className="text-right">{row.invoicedRate}</TableCell>
                      <TableCell>
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={qtyDrafts[idx] ?? (row.qty === 0 ? '' : String(row.qty))}
                          onChange={(e) => handleQtyDraftChange(idx, e.target.value)}
                          className="h-8 w-24 border-primary-200"
                          placeholder=""
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={rateDrafts[idx] ?? (row.rate === 0 ? '' : String(row.rate))}
                          onChange={(e) => handleRateDraftChange(idx, e.target.value)}
                          className="h-8 w-24 border-primary-200"
                          placeholder=""
                        />
                      </TableCell>
                      <TableCell className="text-right">{lineAmount(row).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Credit/Debit Note Summary (Invoice Summary style) */}
              <Card className="mt-6 border-2 border-primary-200 shadow-lg">
                <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-t-xl">
                  <CardTitle className="text-xl font-bold text-primary-800">Note Summary</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div className="flex justify-between text-lg">
                        <span className="font-medium">Subtotal:</span>
                        <span>₹{taxableAmount.toFixed(2)}</span>
                      </div>
                      {!isIgst ? (
                        <>
                          <div className="flex justify-between text-lg">
                            <span className="font-medium">CGST:</span>
                            <span>₹{totalCgst.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-lg">
                            <span className="font-medium">SGST:</span>
                            <span>₹{totalSgst.toFixed(2)}</span>
                          </div>
                        </>
                      ) : (
                        <div className="flex justify-between text-lg">
                          <span className="font-medium">IGST:</span>
                          <span>₹{totalIgst.toFixed(2)}</span>
                        </div>
                      )}
                      <Separator />
                      <div className="flex justify-between text-2xl font-bold text-primary-800">
                        <span>Grand Total:</span>
                        <span>₹{grandTotal.toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label>Amount Chargeable (in words)</Label>
                        <p className="text-sm text-gray-700 border border-gray-200 rounded-xl p-3 bg-gray-50 min-h-[4.5rem]">
                          INR {amountInWords(grandTotal)}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Tax Amount (in words)</Label>
                        <p className="text-sm text-gray-700 border border-gray-200 rounded-xl p-3 bg-gray-50 min-h-[4.5rem]">
                          INR {amountInWords(totalTaxAmount)}
                        </p>
                      </div>
                      <div className="text-sm text-gray-600">
                        Company&apos;s PAN: {branchDetails?.panNo ?? 'N/A'}
                      </div>
                    </div>
                  </div>

                  {hsnWiseTaxDetails.length > 0 && (
                    <div className="mt-6 border rounded-xl overflow-hidden">
                      <table className="w-full border-collapse text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b">
                            <th className="border border-gray-300 p-2 text-left">HSN/SAC</th>
                            <th className="border border-gray-300 p-2 text-left">Taxable Value</th>
                            {!isIgst ? (
                              <>
                                <th className="border border-gray-300 p-2">CGST Rate</th>
                                <th className="border border-gray-300 p-2">CGST Amount</th>
                                <th className="border border-gray-300 p-2">SGST Rate</th>
                                <th className="border border-gray-300 p-2">SGST Amount</th>
                              </>
                            ) : (
                              <>
                                <th className="border border-gray-300 p-2">IGST Rate</th>
                                <th className="border border-gray-300 p-2">IGST Amount</th>
                              </>
                            )}
                            <th className="border border-gray-300 p-2">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hsnWiseTaxDetails.map((row, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="border border-gray-300 p-2">{row.hsn}</td>
                              <td className="border border-gray-300 p-2">{row.taxableValue.toFixed(2)}</td>
                              {!isIgst ? (
                                <>
                                  <td className="border border-gray-300 p-2 text-center">{row.cgstRate.toFixed(2)}%</td>
                                  <td className="border border-gray-300 p-2 text-right">{row.cgstAmount.toFixed(2)}</td>
                                  <td className="border border-gray-300 p-2 text-center">{row.sgstRate.toFixed(2)}%</td>
                                  <td className="border border-gray-300 p-2 text-right">{row.sgstAmount.toFixed(2)}</td>
                                </>
                              ) : (
                                <>
                                  <td className="border border-gray-300 p-2 text-center">{row.igstRate.toFixed(2)}%</td>
                                  <td className="border border-gray-300 p-2 text-right">{row.igstAmount.toFixed(2)}</td>
                                </>
                              )}
                              <td className="border border-gray-300 p-2 text-right">{row.totalTaxAmount.toFixed(2)}</td>
                            </tr>
                          ))}
                          <tr className="bg-gray-50 font-semibold">
                            <td className="border border-gray-300 p-2">TOTAL</td>
                            <td className="border border-gray-300 p-2">{taxableAmount.toFixed(2)}</td>
                            {!isIgst ? (
                              <>
                                <td className="border border-gray-300 p-2"></td>
                                <td className="border border-gray-300 p-2 text-right">{totalCgst.toFixed(2)}</td>
                                <td className="border border-gray-300 p-2"></td>
                                <td className="border border-gray-300 p-2 text-right">{totalSgst.toFixed(2)}</td>
                              </>
                            ) : (
                              <>
                                <td className="border border-gray-300 p-2"></td>
                                <td className="border border-gray-300 p-2 text-right">{totalIgst.toFixed(2)}</td>
                              </>
                            )}
                            <td className="border border-gray-300 p-2 text-right">{totalTaxAmount.toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm text-gray-700">
                      We declare that this note shows the actual price of the goods described and that all particulars are true and correct.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => navigate('/invoices/credit-debit-notes')}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving || selectedLines.length === 0}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {isEdit ? 'Update' : 'Save'} and preview
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
